import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMetrics, useCreateMetric, useUpdateMetric, useDeleteMetric } from '@/hooks/use-metrics'
import { useBusinessAreas } from '@/hooks/use-business-areas'
import { Modal } from '@/components/modal'
import type { Metric } from '@/types'

interface MetricFormData {
  name: string
  description: string
  unit: string
  area_id: string
  display_order: number
  is_hidden: boolean
}

export function AdminMetrics() {
  const navigate = useNavigate()
  const { data: metrics, isLoading } = useMetrics()
  const { data: areas } = useBusinessAreas()
  const createMetric = useCreateMetric()
  const updateMetric = useUpdateMetric()
  const deleteMetric = useDeleteMetric()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingMetric, setEditingMetric] = useState<Metric | null>(null)
  const [formData, setFormData] = useState<MetricFormData>({
    name: '',
    description: '',
    unit: '',
    area_id: '',
    display_order: 0,
    is_hidden: false,
  })

  const openCreate = () => {
    setEditingMetric(null)
    setFormData({ name: '', description: '', unit: '', area_id: '', display_order: (metrics?.length || 0) + 1, is_hidden: false })
    setIsModalOpen(true)
  }

  const openEdit = (metric: Metric) => {
    setEditingMetric(metric)
    setFormData({
      name: metric.name,
      description: metric.description || '',
      unit: metric.unit || '',
      area_id: metric.area_id || '',
      display_order: metric.display_order,
      is_hidden: metric.is_hidden ?? false,
    })
    setIsModalOpen(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const payload = {
      ...formData,
      area_id: formData.area_id || null, // Convert empty string to null
    }
    if (editingMetric) {
      await updateMetric.mutateAsync({ id: editingMetric.id, ...payload })
    } else {
      await createMetric.mutateAsync(payload)
    }
    setIsModalOpen(false)
  }

  const handleDelete = async (id: string) => {
    if (confirm('Delete this metric? This will also delete all associated goals and values.')) {
      await deleteMetric.mutateAsync(id)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manage Metrics</h1>
          <p className="text-sm text-gray-500 mt-1">Define the KPIs your team tracks</p>
        </div>
        <button onClick={openCreate} className="btn btn-primary text-sm px-3 py-1.5">
          + Add Metric
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Order</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Unit</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Area</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Description</th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {metrics?.map((metric) => (
              <tr key={metric.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm text-gray-500">{metric.display_order}</td>
                <td className="px-6 py-4">
                  <button
                    onClick={() => navigate(`/metrics/${metric.id}`)}
                    className="font-medium text-gray-900 hover:text-brand-teal hover:underline text-left"
                  >
                    {metric.name}
                  </button>
                  {!!metric.is_hidden && (
                    <span className="ml-2 text-xs text-gray-400" title="Hidden from scorecard">
                      (hidden)
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">{metric.unit || '—'}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{metric.area_name || '—'}</td>
                <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">{metric.description || '—'}</td>
                <td className="px-6 py-4 text-right space-x-2">
                  <button onClick={() => openEdit(metric)} className="text-brand-teal hover:text-brand-teal/80">
                    Edit
                  </button>
                  <button onClick={() => handleDelete(metric.id)} className="text-danger-600 hover:text-danger-700">
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {(!metrics || metrics.length === 0) && (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                  No metrics defined yet. Create your first metric to get started.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingMetric ? 'Edit Metric' : 'Create Metric'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-brand-teal focus:border-transparent"
              placeholder="e.g., Revenue"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
            <select
              value={formData.unit}
              onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-brand-teal focus:border-transparent"
            >
              <option value="">None</option>
              <option value="$">$ (Currency)</option>
              <option value="%">% (Percentage)</option>
              <option value="#"># (Number)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Business Area</label>
            <select
              value={formData.area_id}
              onChange={(e) => setFormData({ ...formData, area_id: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-brand-teal focus:border-transparent"
            >
              <option value="">None</option>
              {areas?.filter(a => a.is_active).map((area) => (
                <option key={area.id} value={area.id}>{area.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-brand-teal focus:border-transparent"
              rows={2}
              placeholder="What does this metric measure?"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
            <input
              type="number"
              min="1"
              value={formData.display_order}
              onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 1 })}
              className="w-24 rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-brand-teal focus:border-transparent"
            />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_hidden"
                checked={formData.is_hidden}
                onChange={(e) => setFormData({ ...formData, is_hidden: e.target.checked })}
                className="rounded border-gray-300 text-brand-teal focus:ring-brand-teal"
              />
              <label htmlFor="is_hidden" className="text-sm text-gray-700">
                Hide from Scorecard
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Hidden metrics still accept data but don't appear on the dashboard
            </p>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={() => setIsModalOpen(false)} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={createMetric.isPending || updateMetric.isPending}>
              {editingMetric ? 'Save Changes' : 'Create Metric'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
